import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { LOGOS } from "../lib/logos";

const SOULTRIP_MARK = LOGOS.soulTripMark;

/**
 * SOUL TRIP — CINEMATIC EARTH HERO
 *
 * Phases (driven by elapsed time after camera ready):
 *   0  → starfield only, deep space (0–2.0s)
 *   1  → Earth fades in, camera distant (2.0–4.5s)
 *   2  → SOUL TRIP title floats in over rotating Earth (4.5–8.5s)
 *   3  → Slow cinematic zoom toward India + camera tilt (8.5–14s)
 *   4  → Interactive: drag to rotate, pins glow, scroll hint appears
 *
 * Realism stack:
 *   - 8K NASA blue-marble day texture (continents/oceans)
 *   - Night-side city lights (composited via emissive map)
 *   - Cloud layer (separate sphere, slightly larger, rotates faster)
 *   - Atmosphere scattering shader (back-side glow)
 *   - Procedural ~6000 star field
 *   - Sun rim lighting
 */
export const CinematicEarth = ({ episodes = [], futurePins = [], onSelect, onReady }) => {
  const mountRef = useRef(null);
  const tooltipRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [phase, setPhase] = useState(0); // 0..4
  const [interactive, setInteractive] = useState(false);
  const [skipped, setSkipped] = useState(false);

  // Refs for the imperative skip handler
  const phaseRef = useRef(0);
  const skipRef = useRef(false);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { skipRef.current = skipped; }, [skipped]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const W = () => mount.clientWidth;
    const H = () => mount.clientHeight;

    // ── Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(W(), H());
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    // ── Scene + Camera
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x02030a);

    const camera = new THREE.PerspectiveCamera(38, W() / H(), 0.1, 2000);
    camera.position.set(0, 0, 7.2); // far away for opening

    // ── Star field (procedural, 6000 stars, varying size + color temperature)
    {
      const starsGeo = new THREE.BufferGeometry();
      const N = 6000;
      const pos = new Float32Array(N * 3);
      const col = new Float32Array(N * 3);
      const siz = new Float32Array(N);
      for (let i = 0; i < N; i++) {
        // distribute on a large sphere shell
        const r = 600 + Math.random() * 200;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        pos[i*3+0] = r * Math.sin(phi) * Math.cos(theta);
        pos[i*3+1] = r * Math.cos(phi);
        pos[i*3+2] = r * Math.sin(phi) * Math.sin(theta);
        // colour temperature: most white, some warm/cold
        const t = Math.random();
        if (t < 0.7)      { col[i*3+0] = 1.0; col[i*3+1] = 1.0; col[i*3+2] = 1.0; }
        else if (t < 0.85){ col[i*3+0] = 1.0; col[i*3+1] = 0.86; col[i*3+2] = 0.66; }
        else              { col[i*3+0] = 0.66; col[i*3+1] = 0.82; col[i*3+2] = 1.0; }
        siz[i] = 0.6 + Math.pow(Math.random(), 4) * 4.5;
      }
      starsGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      starsGeo.setAttribute("color", new THREE.BufferAttribute(col, 3));
      starsGeo.setAttribute("aSize", new THREE.BufferAttribute(siz, 1));

      const starsMat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uFade: { value: 0 } },
        vertexShader: `
          attribute float aSize;
          varying vec3 vColor;
          uniform float uTime;
          void main() {
            vColor = color;
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = aSize * (260.0 / -mv.z);
            gl_Position = projectionMatrix * mv;
          }
        `,
        fragmentShader: `
          varying vec3 vColor;
          uniform float uFade;
          void main() {
            vec2 c = gl_PointCoord - 0.5;
            float d = length(c);
            float a = smoothstep(0.5, 0.0, d);
            gl_FragColor = vec4(vColor, a * uFade);
          }
        `,
        transparent: true,
        depthWrite: false,
        vertexColors: true,
        blending: THREE.AdditiveBlending,
      });
      const stars = new THREE.Points(starsGeo, starsMat);
      scene.add(stars);
      // store ref via userData for animate loop
      scene.userData.starsMat = starsMat;
      scene.userData.stars = stars;
    }

    // ── Subtle nebula plane far behind (gradient cloud)
    {
      const nebGeo = new THREE.PlaneGeometry(2400, 1400);
      const nebMat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uFade: { value: 0 } },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          varying vec2 vUv;
          uniform float uTime;
          uniform float uFade;
          // tiny hash noise
          float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
          float noise(vec2 p) {
            vec2 i = floor(p); vec2 f = fract(p);
            float a = hash(i);
            float b = hash(i + vec2(1.0,0.0));
            float c = hash(i + vec2(0.0,1.0));
            float d = hash(i + vec2(1.0,1.0));
            vec2 u = f*f*(3.0-2.0*f);
            return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
          }
          void main() {
            vec2 uv = vUv * 4.0;
            float n = 0.0;
            n += noise(uv) * 0.5;
            n += noise(uv * 2.0) * 0.25;
            n += noise(uv * 4.0) * 0.125;
            vec3 col1 = vec3(0.04, 0.02, 0.10);   // purple
            vec3 col2 = vec3(0.02, 0.05, 0.14);   // deep blue
            vec3 col3 = vec3(0.18, 0.08, 0.04);   // ember
            vec3 col = mix(col1, col2, n);
            col += col3 * pow(n, 4.0) * 0.6;
            float vignette = smoothstep(1.0, 0.2, length(vUv - 0.5));
            gl_FragColor = vec4(col * vignette * 0.85, uFade * 0.65);
          }
        `,
        transparent: true,
        depthWrite: false,
      });
      const nebula = new THREE.Mesh(nebGeo, nebMat);
      nebula.position.z = -900;
      scene.add(nebula);
      scene.userData.nebulaMat = nebMat;
    }

    // ── Earth group (so we can rotate while camera moves independently)
    const earthGroup = new THREE.Group();
    earthGroup.rotation.z = -0.4093; // 23.5° axial tilt
    scene.add(earthGroup);

    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");

    // EARTH base sphere — day + night composite (custom shader)
    const earthGeo = new THREE.SphereGeometry(1, 128, 128);
    const earthUniforms = {
      uDayMap:    { value: null },
      uNightMap:  { value: null },
      uBumpMap:   { value: null },
      uSpecMap:   { value: null },
      uSunDir:    { value: new THREE.Vector3(5, 1.6, 4).normalize() },
      uReady:     { value: 0 },        // 0..1 fade-in
    };
    const earthMat = new THREE.ShaderMaterial({
      uniforms: earthUniforms,
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vWorldNormal;
        varying vec3 vWorldPos;
        void main() {
          vUv = uv;
          vNormal = normalize(normalMatrix * normal);
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          vWorldNormal = normalize(mat3(modelMatrix) * normal);
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: `
        uniform sampler2D uDayMap;
        uniform sampler2D uNightMap;
        uniform sampler2D uSpecMap;
        uniform vec3 uSunDir;
        uniform float uReady;
        varying vec2 vUv;
        varying vec3 vWorldNormal;

        void main() {
          vec3 dayColor   = texture2D(uDayMap,   vUv).rgb;
          vec3 nightColor = texture2D(uNightMap, vUv).rgb;
          float specMask  = texture2D(uSpecMap,  vUv).r;

          float sunDot = dot(normalize(vWorldNormal), normalize(uSunDir));
          float dayMix = smoothstep(-0.18, 0.22, sunDot);

          // Slightly brighten day; let night city lights shine
          vec3 day = dayColor * (0.85 + 0.25 * dayMix);
          // Subtle ocean specular at terminator
          float term = 1.0 - abs(sunDot);
          day += vec3(0.6, 0.7, 0.9) * specMask * term * 0.15;

          vec3 night = nightColor * 1.4;
          vec3 col = mix(night, day, dayMix);

          gl_FragColor = vec4(col * uReady, 1.0);
        }
      `,
    });

    // Use a placeholder while textures load — solid dark blue
    earthUniforms.uDayMap.value   = makePlaceholderTexture("#1f3a5f");
    earthUniforms.uNightMap.value = makePlaceholderTexture("#080820");
    earthUniforms.uSpecMap.value  = makePlaceholderTexture("#000000");

    loader.load("https://unpkg.com/three-globe@2.31.1/example/img/earth-blue-marble.jpg",
      (t) => { t.colorSpace = THREE.SRGBColorSpace; earthUniforms.uDayMap.value = t; });
    loader.load("https://unpkg.com/three-globe@2.31.1/example/img/earth-night.jpg",
      (t) => { t.colorSpace = THREE.SRGBColorSpace; earthUniforms.uNightMap.value = t; });
    loader.load("https://unpkg.com/three-globe@2.31.1/example/img/earth-water.png",
      (t) => { earthUniforms.uSpecMap.value = t; });

    const earth = new THREE.Mesh(earthGeo, earthMat);
    earthGroup.add(earth);

    // ── Cloud layer (slightly larger sphere, rotates faster)
    const cloudGeo = new THREE.SphereGeometry(1.015, 96, 96);
    const cloudMat = new THREE.MeshPhongMaterial({
      transparent: true,
      opacity: 0,           // fade in
      depthWrite: false,
      alphaMap: null,
      color: 0xffffff,
    });
    loader.load("https://unpkg.com/three-globe@2.31.1/example/img/earth-topology.png",
      (t) => { cloudMat.alphaMap = t; cloudMat.needsUpdate = true; });
    const clouds = new THREE.Mesh(cloudGeo, cloudMat);
    earthGroup.add(clouds);

    // ── Atmosphere glow (BackSide shader)
    const atmoGeo = new THREE.SphereGeometry(1.085, 96, 96);
    const atmoMat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0x6ab6e8) },
        uReady: { value: 0 },
      },
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      vertexShader: `
        varying float intensity;
        void main() {
          vec3 vNormal = normalize(normalMatrix * normal);
          intensity = pow(0.62 - dot(vNormal, vec3(0.0,0.0,1.0)), 2.4);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uReady;
        varying float intensity;
        void main() {
          vec3 glow = uColor * intensity;
          gl_FragColor = vec4(glow, intensity * uReady);
        }
      `,
    });
    const atmosphere = new THREE.Mesh(atmoGeo, atmoMat);
    earthGroup.add(atmosphere);

    // Warm outer rim
    const rimGeo = new THREE.SphereGeometry(1.25, 64, 64);
    const rimMat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(0xC9A04A) }, uReady: { value: 0 } },
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      vertexShader: `
        varying float intensity;
        void main() {
          vec3 vNormal = normalize(normalMatrix * normal);
          intensity = pow(0.55 - dot(vNormal, vec3(0.0,0.0,1.0)), 4.0);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uReady;
        varying float intensity;
        void main() {
          gl_FragColor = vec4(uColor * intensity, intensity * uReady * 0.7);
        }
      `,
    });
    const rim = new THREE.Mesh(rimGeo, rimMat);
    earthGroup.add(rim);

    // ── Pins (gold for S1, blue holographic for S2)
    const pinGroup = new THREE.Group();
    earthGroup.add(pinGroup);
    const pinMeshes = [];

    const latLngToVec3 = (lat, lng, radius = 1.0) => {
      const phi = (90 - lat) * (Math.PI / 180);
      const theta = (lng + 180) * (Math.PI / 180);
      return new THREE.Vector3(
        -radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.cos(phi),
        radius * Math.sin(phi) * Math.sin(theta),
      );
    };

    const buildPin = (ep, isS2 = false) => {
      const pos = latLngToVec3(ep.lat, ep.lng, 1.0);
      const colorHex = isS2 ? 0x9ED4F2 : 0xFFE08A;
      const ringColor = isS2 ? 0x6AB6E8 : 0xC9A04A;

      // Needle
      const needleGeo = new THREE.CylinderGeometry(0.0028, 0.0028, 0.09, 8);
      const needleMat = new THREE.MeshBasicMaterial({ color: ringColor });
      const needle = new THREE.Mesh(needleGeo, needleMat);
      needle.position.copy(pos.clone().multiplyScalar(1.05));
      needle.lookAt(0,0,0);
      needle.rotateX(Math.PI/2);
      pinGroup.add(needle);

      // Head (clickable)
      const headGeo = new THREE.SphereGeometry(0.022, 20, 20);
      const headMat = new THREE.MeshBasicMaterial({ color: colorHex });
      const head = new THREE.Mesh(headGeo, headMat);
      head.position.copy(pos.clone().multiplyScalar(1.105));
      head.userData = { episode: ep, isS2 };
      pinGroup.add(head);
      pinMeshes.push(head);

      // Halo
      const haloGeo = new THREE.SphereGeometry(0.034, 16, 16);
      const haloMat = new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.28 });
      const halo = new THREE.Mesh(haloGeo, haloMat);
      halo.position.copy(pos.clone().multiplyScalar(1.105));
      pinGroup.add(halo);

      // Surface ring
      const ringGeo = new THREE.RingGeometry(0.028, 0.044, 32);
      const ringMat = new THREE.MeshBasicMaterial({ color: ringColor, side: THREE.DoubleSide, transparent: true, opacity: 0.7 });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.copy(pos.clone().multiplyScalar(1.003));
      ring.lookAt(pos.clone().multiplyScalar(2));
      pinGroup.add(ring);
    };

    episodes.forEach((e) => buildPin(e, false));
    futurePins.forEach((p) => buildPin(p, true));

    // Hide pins until phase 3+
    pinGroup.visible = false;
    pinGroup.scale.setScalar(0.001);

    // ── Interaction state
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let dragging = false;
    let lastX = 0, lastY = 0;
    let rotVel = { x: 0, y: 0 };
    let userRotation = { x: 0, y: 0 };
    let autoRotate = true;

    const onPointerDown = (e) => {
      if (!interactiveRef.current) return;
      dragging = true;
      lastX = e.clientX; lastY = e.clientY;
      autoRotate = false;
      mount.style.cursor = "grabbing";
    };
    const onPointerMove = (e) => {
      const rect = mount.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / W()) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / H()) * 2 + 1;

      if (dragging) {
        const dx = e.clientX - lastX;
        const dy = e.clientY - lastY;
        rotVel.y = dx * 0.005;
        rotVel.x = dy * 0.003;
        userRotation.y += rotVel.y;
        userRotation.x = Math.max(-0.9, Math.min(0.9, userRotation.x + rotVel.x));
        lastX = e.clientX; lastY = e.clientY;
      }

      // Hover detection (only in interactive phase)
      if (interactiveRef.current) {
        raycaster.setFromCamera(mouse, camera);
        const hits = raycaster.intersectObjects(pinMeshes);
        if (hits.length > 0) {
          const ep = hits[0].object.userData.episode;
          if (tooltipRef.current) {
            tooltipRef.current.style.left = `${e.clientX - rect.left}px`;
            tooltipRef.current.style.top  = `${e.clientY - rect.top}px`;
          }
          setHovered({ ...ep, isS2: hits[0].object.userData.isS2 });
          mount.style.cursor = "pointer";
        } else {
          setHovered(null);
          mount.style.cursor = dragging ? "grabbing" : "grab";
        }
      }
    };
    const onPointerUp = () => {
      dragging = false;
      mount.style.cursor = "grab";
      setTimeout(() => { autoRotate = true; }, 2500);
    };
    const onClick = (e) => {
      if (!interactiveRef.current) return;
      const rect = mount.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / W()) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / H()) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(pinMeshes);
      if (hits.length > 0 && !hits[0].object.userData.isS2 && onSelect) {
        onSelect(hits[0].object.userData.episode);
      }
    };
    const interactiveRef = { current: false };

    mount.addEventListener("pointerdown", onPointerDown);
    mount.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    mount.addEventListener("click", onClick);

    const onResize = () => {
      camera.aspect = W() / H();
      camera.updateProjectionMatrix();
      renderer.setSize(W(), H());
    };
    window.addEventListener("resize", onResize);

    // ── Cinematic timeline
    const startTime = performance.now();
    // INDIA target rotation (so India faces the camera at end of zoom)
    const INDIA_ROT_Y = 3.1;

    let phaseLocal = 0;
    const setPhaseSafe = (p) => {
      if (p !== phaseLocal) {
        phaseLocal = p;
        setPhase(p);
        if (p === 4) {
          interactiveRef.current = true;
          setInteractive(true);
          onReady?.();
        }
      }
    };

    const smoothstep = (a, b, x) => {
      x = Math.max(0, Math.min(1, (x - a) / (b - a)));
      return x * x * (3 - 2 * x);
    };
    const lerp = (a, b, t) => a + (b - a) * t;

    let frameId;
    const animate = (now) => {
      const elapsed = (now - startTime) / 1000;
      const t = skipRef.current ? 14 : elapsed; // skip → jump to interactive

      // ── Stars/nebula fade in
      if (scene.userData.starsMat)  scene.userData.starsMat.uniforms.uFade.value  = smoothstep(0, 1.5, t);
      if (scene.userData.nebulaMat) scene.userData.nebulaMat.uniforms.uFade.value = smoothstep(0.3, 1.8, t);
      if (scene.userData.stars) scene.userData.stars.rotation.y = t * 0.001;

      // ── Earth fade-in (phase 1)
      const earthReady = smoothstep(2.0, 4.5, t);
      earthUniforms.uReady.value = earthReady;
      atmoMat.uniforms.uReady.value = earthReady;
      rimMat.uniforms.uReady.value  = earthReady;
      cloudMat.opacity = smoothstep(3.5, 6.0, t) * 0.55;

      // ── Camera dolly (phase 1 → phase 3)
      // 7.2 (start) → 4.2 (title) → 2.6 (zoomed on India)
      let camZ;
      if (t < 4.5) camZ = lerp(7.2, 5.0, smoothstep(2.0, 4.5, t));
      else if (t < 8.5) camZ = lerp(5.0, 4.2, smoothstep(4.5, 8.5, t));
      else camZ = lerp(4.2, 2.6, smoothstep(8.5, 13.5, t));
      camera.position.z = camZ;

      // Slight orbit drift (parallax)
      camera.position.x = Math.sin(t * 0.05) * 0.15;
      camera.position.y = Math.cos(t * 0.04) * 0.08;
      camera.lookAt(0, 0, 0);

      // ── Earth rotation
      if (t < 8.5) {
        // Free slow rotation
        earthGroup.rotation.y = 0.6 + t * 0.07;
      } else if (t < 14) {
        // Ease into India-facing rotation
        const k = smoothstep(8.5, 14, t);
        const target = INDIA_ROT_Y;
        const startRot = 0.6 + 8.5 * 0.07;
        earthGroup.rotation.y = lerp(startRot, target, k);
      } else {
        // Interactive
        if (autoRotate) userRotation.y += 0.0015;
        earthGroup.rotation.y = INDIA_ROT_Y + userRotation.y;
        earthGroup.rotation.x = 0.25 + userRotation.x;
      }

      // Cloud rotation always slightly faster than earth
      clouds.rotation.y = earthGroup.rotation.y * 1.05 + t * 0.005;

      // ── Reveal pins after camera reaches India (phase 3.5+)
      const pinReveal = smoothstep(12.0, 14.5, t);
      if (pinReveal > 0) {
        pinGroup.visible = true;
        pinGroup.scale.setScalar(pinReveal);
        // Gentle pulse on pin heads (smaller amplitude so they don't bloom huge)
        pinMeshes.forEach((p, i) => {
          const s = pinReveal * (1 + Math.sin(t * 2.4 + i) * 0.08);
          p.scale.setScalar(s);
        });
      }

      // ── Phase tracking (for UI overlays)
      if      (t < 2.0)  setPhaseSafe(0);
      else if (t < 4.5)  setPhaseSafe(1);
      else if (t < 8.5)  setPhaseSafe(2);
      else if (t < 14.0) setPhaseSafe(3);
      else               setPhaseSafe(4);

      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    frameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointerup", onPointerUp);
      mount.removeEventListener("pointerdown", onPointerDown);
      mount.removeEventListener("pointermove", onPointerMove);
      mount.removeEventListener("click", onClick);
      mount.removeChild(renderer.domElement);
      renderer.dispose();
      earthGeo.dispose(); earthMat.dispose();
      cloudGeo.dispose(); cloudMat.dispose();
      atmoGeo.dispose(); atmoMat.dispose();
      rimGeo.dispose(); rimMat.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={mountRef} data-testid="cinematic-earth-canvas" className="absolute inset-0 w-full h-full" style={{ cursor: interactive ? "grab" : "default" }}>
      {/* Tooltip for pin hover */}
      <div
        ref={tooltipRef}
        className="absolute pointer-events-none -translate-x-1/2 -translate-y-[120%]"
        style={{
          opacity: hovered ? 1 : 0,
          transition: "opacity .2s",
          background: "rgba(8,6,4,.92)",
          backdropFilter: "blur(12px)",
          border: `1px solid ${hovered?.isS2 ? "rgba(106,182,232,.5)" : "rgba(201,160,74,.5)"}`,
          padding: "12px 16px",
          minWidth: 220,
          zIndex: 30,
        }}
      >
        {hovered && (
          <>
            <div className="st-cinzel text-[0.5rem] tracking-[0.4em] uppercase mb-1"
                 style={{ color: hovered.isS2 ? "#9ED4F2" : "var(--gold)" }}>
              {hovered.isS2 ? "S/02 · INBOUND" : `Episode ${String(hovered.number).padStart(2, "0")}`}
            </div>
            <div className="st-cormorant text-base font-semibold text-[var(--cream)]">
              {hovered.guest || hovered.label}
            </div>
            <div className="text-[0.78rem] text-[var(--fog)]">
              {hovered.location || hovered.region}
            </div>
            {!hovered.isS2 && (
              <div className="mt-2 st-cinzel text-[0.48rem] tracking-[0.3em] uppercase text-[var(--gold)]">
                Click to watch trailer ↗
              </div>
            )}
          </>
        )}
      </div>

      {/* Title overlay — visible during phases 2-3 */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-4 text-center"
        style={{
          zIndex: 5,
          opacity: phase >= 2 && phase < 4 ? 1 : 0,
          transition: "opacity 1.2s ease",
        }}
      >
        <div
          className="st-cinzel text-[0.5rem] md:text-[0.58rem] tracking-[0.55em] uppercase"
          style={{ color: "rgba(232,197,106,.7)", textShadow: "0 2px 18px rgba(0,0,0,.95)" }}
        >
          GANDABHERUNDA ARTS · PRESENTS
        </div>

        {/* Painted Soul Trip wordmark — official white PNG */}
        <img
          src={SOULTRIP_MARK}
          alt="Soul Trip"
          className="mt-5 block w-auto"
          style={{
            height: "clamp(140px, 26vw, 320px)",
            maxWidth: "min(900px, 92vw)",
            filter:
              "drop-shadow(0 6px 60px rgba(232,197,106,.55)) drop-shadow(0 12px 50px rgba(0,0,0,.9))",
            animation: phase >= 2 ? "titleEntrance 1.6s cubic-bezier(.2,.7,.2,1) both" : "none",
          }}
        />

        <div
          className="mt-3 st-cinzel text-[0.55rem] md:text-[0.66rem] tracking-[0.42em] uppercase"
          style={{ color: "rgba(244,237,216,.78)", textShadow: "0 2px 14px rgba(0,0,0,.9)" }}
        >
          REAL LOCATIONS · REAL CONVERSATIONS · REAL VIBES
        </div>
        <style>{`
          @keyframes titleEntrance {
            from { opacity: 0; transform: translateY(40px) scale(.94); filter: blur(8px) drop-shadow(0 6px 60px rgba(201,160,74,.35)); }
            to   { opacity: 1; transform: none;                       filter: blur(0)   drop-shadow(0 6px 60px rgba(201,160,74,.35)) drop-shadow(0 12px 50px rgba(0,0,0,.85)); }
          }
        `}</style>
      </div>

      {/* Skip button — visible during phases 0–3 */}
      {phase < 4 && (
        <button
          data-testid="cinematic-skip"
          onClick={() => setSkipped(true)}
          className="absolute bottom-6 right-6 st-cinzel text-[0.48rem] tracking-[0.35em] uppercase px-3 py-2 st-interactive"
          style={{
            zIndex: 8,
            background: "rgba(5,4,3,.55)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(201,160,74,.4)",
            color: "var(--gold-lt)",
          }}
        >
          Skip Intro ▸
        </button>
      )}

      {/* Phase indicator at left edge */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-2" style={{ zIndex: 8 }}>
        {["DEEP SPACE", "EARTH RISE", "TITLE", "DIVE → INDIA", "EXPLORE"].map((label, i) => (
          <div key={label} className="flex items-center gap-3">
            <span
              className="block w-2 h-2 rounded-full transition-all"
              style={{
                background: phase >= i ? "#E8C56A" : "rgba(244,237,216,.18)",
                boxShadow: phase === i ? "0 0 8px rgba(232,197,106,.85)" : "none",
              }}
            />
            <span
              className="st-cinzel text-[0.42rem] tracking-[0.32em] uppercase"
              style={{ color: phase >= i ? "var(--gold-lt)" : "rgba(244,237,216,.28)" }}
            >
              {String(i).padStart(2,"0")} · {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Solid-color 1x1 texture as a safe placeholder until the real ones load
function makePlaceholderTexture(cssColor) {
  const c = document.createElement("canvas");
  c.width = 2; c.height = 2;
  const ctx = c.getContext("2d");
  ctx.fillStyle = cssColor;
  ctx.fillRect(0, 0, 2, 2);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
